import type { IVector3 } from '@seddi/math-v2';
import { BufferAttribute, BufferGeometry, DynamicDrawUsage, Vector3 } from '@seddi/three';
import type { VertexDataDesc } from '../../domain/bufferTypes';

/**
 * Create indices buffer.
 * @param self - Geometry.
 * @param data - Indices data.
 */
export const createIndex = (self: BufferGeometry, data: Readonly<Uint32Array>) => {
  const attribute = new BufferAttribute(data, 1);
  self.setIndex(attribute);
};

/**
 * Updates indices buffer.
 * @param self - Geometry.
 * @param data - Indices.
 */
export const updateIndex = (self: BufferGeometry, data: Readonly<Uint32Array>) => {
  // NOTE: Currently we cant reuse the previous buffer
  // Chrome 96 seems to be incompatible with IndexBuffer DYNAMIC_DRAW
  // let attribute: BufferAttribute = self.index;
  // attribute.setUsage(DynamicDrawUsage);
  const attribute = new BufferAttribute(data, 1);
  self.setIndex(attribute);
};

/**
 * Create vertex attribute.
 * @param self - Geometry.
 * @param key - Key.
 * @param data - Data.
 * @param itemSize - Item size.
 */
export const createVertexAttributeF32 = (
  self: BufferGeometry,
  key: string,
  data: Readonly<Float32Array>,
  itemSize: number,
) => {
  const attribute = new BufferAttribute(data, itemSize);
  self.setAttribute(key, attribute);
};

/**
 * Update vertex attribute reusing previous buffer if possible.
 * @param self - Geometry.
 * @param key - Key.
 * @param data - Data.
 * @param itemSize - Item size.
 */
export const updateVertexAttributeF32 = (
  self: BufferGeometry,
  key: string,
  data: Readonly<Float32Array>,
  itemSize: number,
) => {
  let attribute: BufferAttribute = self.getAttribute(key) as BufferAttribute;

  if (!attribute || attribute.array.length !== data.length) {
    // Recreate.
    // data = new Float32Array(data);
    attribute = new BufferAttribute(data, itemSize);
    attribute.setUsage(DynamicDrawUsage);
    self.setAttribute(key, attribute);
  } else {
    // Update in-place, do not use copyArray as it violates immutability of state.
    attribute.array = data;
  }

  attribute.needsUpdate = true;
};

/**
 * Recompute vertex normals reusing previous buffer if possible.
 * @param self - Geometry.
 */
export const computeVertexNormalsF32 = (self: BufferGeometry) => {
  const positionAttribute = self.getAttribute('position');
  const positions = positionAttribute.array as Float32Array;
  const indices = self.index.array as Uint32Array;

  let normalAttribute = self.getAttribute('normal');
  let normals;
  if (!normalAttribute || normalAttribute.array.length !== positionAttribute.array.length) {
    normalAttribute = new BufferAttribute(new Float32Array(positions.length), 3);
    normals = normalAttribute.array as Float32Array;
    self.setAttribute('normal', normalAttribute);
  } else {
    // Reuse existing attribute.
    normals = normalAttribute.array as Float32Array;
    normals.fill(0);
  }

  const pA = new Vector3();
  const pB = new Vector3();
  const pC = new Vector3();
  const cb = new Vector3();
  const ab = new Vector3();

  for (let i = 0, il = indices.length; i < il; i += 3) {
    const oA = indices[i] * 3;
    const oB = indices[i + 1] * 3;
    const oC = indices[i + 2] * 3;

    pA.fromArray(positions, oA);
    pB.fromArray(positions, oB);
    pC.fromArray(positions, oC);

    cb.subVectors(pC, pB);
    ab.subVectors(pA, pB);
    cb.cross(ab);

    addToArray3(normals, oA, cb);
    addToArray3(normals, oB, cb);
    addToArray3(normals, oC, cb);
  }

  // Normalize normals.
  for (let i = 0, il = normals.length; i < il; i += 3) {
    cb.fromArray(normals, i);
    cb.normalize();
    cb.toArray(normals, i);
  }

  normalAttribute.needsUpdate = true;
};

/**
 * Computes per-vertex smooth normals by accumulating unnormalized face normals
 * at each shared vertex, then normalizing. Vertices unreferenced by any triangle
 * keep a zero normal.
 */
export function computeVertexNormalsAreaF32(self: BufferGeometry): Float32Array {
  const positionAttribute = self.getAttribute('position');
  const positions = positionAttribute.array as Float32Array;
  const indices = self.index.array as Uint32Array;

  let normalAttribute = self.getAttribute('normal');
  let normals;
  if (!normalAttribute || normalAttribute.array.length !== positionAttribute.array.length) {
    normalAttribute = new BufferAttribute(new Float32Array(positions.length), 3);
    normals = normalAttribute.array as Float32Array;
    self.setAttribute('normal', normalAttribute);
  } else {
    // Reuse existing attribute.
    normals = normalAttribute.array as Float32Array;
    normals.fill(0);
  }

  for (let i = 0, ni = indices.length; i < ni; i += 3) {
    const i0 = indices[i] * 3;
    const i1 = indices[i + 1] * 3;
    const i2 = indices[i + 2] * 3;

    const ax = positions[i0];
    const ay = positions[i0 + 1];
    const az = positions[i0 + 2];
    const bx = positions[i1];
    const by = positions[i1 + 1];
    const bz = positions[i1 + 2];
    const cx = positions[i2];
    const cy = positions[i2 + 1];
    const cz = positions[i2 + 2];

    const e1x = bx - ax;
    const e1y = by - ay;
    const e1z = bz - az;
    const e2x = cx - ax;
    const e2y = cy - ay;
    const e2z = cz - az;

    // Face normal via cross product (not normalized — area weighting is free)
    const nx = e1y * e2z - e1z * e2y;
    const ny = e1z * e2x - e1x * e2z;
    const nz = e1x * e2y - e1y * e2x;

    normals[i0] += nx;
    normals[i0 + 1] += ny;
    normals[i0 + 2] += nz;
    normals[i1] += nx;
    normals[i1 + 1] += ny;
    normals[i1 + 2] += nz;
    normals[i2] += nx;
    normals[i2 + 1] += ny;
    normals[i2 + 2] += nz;
  }

  for (let i = 0, ni = positions.length / 3; i < ni; i++) {
    const j = i * 3;
    const nx = normals[j],
      ny = normals[j + 1],
      nz = normals[j + 2];
    const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
    if (len > 0) {
      normals[j] = nx / len;
      normals[j + 1] = ny / len;
      normals[j + 2] = nz / len;
    }
  }

  return normals;
}

/**
 * Create buffer geometry.
 * @param data - Geometry data.
 */
export const create = (
  data: VertexDataDesc,
  options?: { areaWeightedNormals?: boolean; computeBoundingSphere?: boolean },
): BufferGeometry => {
  const self = new BufferGeometry();

  const indexAttribute = new BufferAttribute(data.indices, 1);
  self.setIndex(indexAttribute);
  const positionAttribute = new BufferAttribute(data.positions, 3);
  self.setAttribute('position', positionAttribute);

  if (data.normals) {
    const normalAttribute = new BufferAttribute(data.normals, 3);
    self.setAttribute('normal', normalAttribute);
  } else {
    const computeNormals = options?.areaWeightedNormals
      ? computeVertexNormalsAreaF32
      : computeVertexNormalsF32;
    computeNormals(self);
  }

  if (data.uvs) {
    const uvAttribute = new BufferAttribute(data.uvs, 2);
    self.setAttribute('uv', uvAttribute);
  }

  // Recompute bounding sphere at runtime
  if (options?.computeBoundingSphere) {
    self.computeBoundingSphere();
  }

  return self;
};

const addToArray3 = (array: Float32Array, offset: number, value: IVector3) => {
  array[offset] += value.x;
  array[offset + 1] += value.y;
  array[offset + 2] += value.z;
};
